import { forwardRef } from 'react';
import type { GeneratedExamPaper } from '../../types/generated';

interface A4PaperProps {
  paper: GeneratedExamPaper;
}

// A4 at 96dpi: 794px × 1123px
export const A4Paper = forwardRef<HTMLDivElement, A4PaperProps>(({ paper }, ref) => {
  const isWritten = paper.examType === 'written';

  return (
    <div
      ref={ref}
      id="a4-paper"
      style={{
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: '13px',
        color: '#000000',
        padding: '60px 64px',
        boxSizing: 'border-box',
        lineHeight: '1.6',
      }}
    >
      {/* Header */}
      <div
        style={{
          textAlign: 'center',
          borderBottom: '2px solid #000',
          paddingBottom: '12px',
          marginBottom: '16px',
        }}
      >
        {paper.institutionName && (
          <p style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '2px' }}>
            {paper.institutionName}
          </p>
        )}
        <p style={{ fontSize: '11px', marginBottom: '2px', letterSpacing: '0.5px' }}>
          Bangladesh — Higher Secondary Certificate Examination
        </p>
        <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: '4px 0' }}>
          {paper.subject}
          {paper.subjectCode && (
            <span
              style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '8px', color: '#444' }}
            >
              ({paper.subjectCode})
            </span>
          )}
        </h1>
        <p style={{ fontSize: '13px', margin: '2px 0' }}>{paper.paper}</p>
        <p style={{ fontSize: '11px', color: '#444', marginTop: '4px' }}>
          Chapter{paper.chapters.length > 1 ? 's' : ''}: {paper.chapters.join(', ')}
        </p>
      </div>

      {/* Meta */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          marginBottom: '20px',
          borderBottom: '1px solid #ccc',
          paddingBottom: '8px',
        }}
      >
        <span>
          Type: <strong>{paper.examType === 'written' ? 'Creative (Written)' : 'MCQ'}</strong>
        </span>
        <span>
          Duration: <strong>{paper.duration} min</strong>
        </span>
        <span>
          Total Marks: <strong>{paper.totalMarks}</strong>
        </span>
        <span>
          Questions: <strong>{paper.totalQuestions}</strong>
        </span>
        <span>
          Version: <strong>{paper.version}</strong>
        </span>
      </div>

      {/* Instructions */}
      <div
        style={{
          border: '1px solid #000',
          padding: '8px 12px',
          marginBottom: '20px',
          fontSize: '12px',
        }}
      >
        <strong>Instructions:</strong>{' '}
        {isWritten
          ? 'Answer any selected questions. Each creative question has four parts (ক, খ, গ, ঘ). Read all questions carefully before writing.'
          : 'Choose the best answer for each question. Each correct answer carries 1 mark. No negative marking.'}
      </div>

      {/* Questions */}
      {isWritten ? (
        <div>
          {paper.questions.map((q) => (
            <div key={q.number} style={{ marginBottom: '28px', pageBreakInside: 'avoid' }}>
              <p
                style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '8px',
                  borderBottom: '1px dashed #ccc',
                  paddingBottom: '4px',
                }}
              >
                Question {q.number}
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '12px',
                    color: '#555',
                    marginLeft: '8px',
                  }}
                >
                  [Chapter: {q.chapter}]
                </span>
              </p>
              {q.stimulus && (
                <div
                  style={{
                    border: '1px solid #999',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    marginBottom: '10px',
                    backgroundColor: '#f9f9f9',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ fontWeight: 'bold', marginRight: '6px' }}>
                    {paper.version === 'Bangla' ? 'উদ্দীপক:' : 'Stimulus:'}
                  </span>
                  {q.stimulus}
                </div>
              )}
              {q.parts.map((part) => (
                <div
                  key={part.label}
                  style={{ display: 'flex', gap: '12px', marginBottom: '6px', paddingLeft: '8px' }}
                >
                  <span
                    style={{
                      minWidth: '24px',
                      height: '24px',
                      background: '#000',
                      color: '#fff',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  >
                    {part.label}
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0 }}>{part.question}</p>
                    <span style={{ fontSize: '11px', color: '#666' }}>
                      [{part.marks} mark{part.marks > 1 ? 's' : ''}]
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div>
          {paper.questions.map((q) => (
            <div key={q.number} style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
              <span style={{ minWidth: '24px', fontWeight: 'bold', flexShrink: 0 }}>
                {q.number}.
              </span>
              <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{q.parts[0]?.question}</p>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid #ccc',
          marginTop: '32px',
          paddingTop: '8px',
          fontSize: '10px',
          color: '#888',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>Generated: {paper.generatedAt}</span>
        <span>AI Question Generator — HSC Curriculum</span>
      </div>
    </div>
  );
});

A4Paper.displayName = 'A4Paper';
